import { useMatches } from '@tanstack/react-router'
import type { StaticDataRouteOption } from '@tanstack/router-core'

type StaticDataSelector<TValue> = (staticData: StaticDataRouteOption) => TValue | undefined

/**
 * Merges staticData from all route matches in the hierarchy.
 * Child route values override parent route values.
 * Explicitly setting a value to undefined in a child route will override the parent value.
 *
 * @param selector - Function to extract value from staticData (e.g., (data) => data.header)
 * @param defaultValue - Default value if no matches have the specified key
 * @returns Deeply merged configuration from all matching routes
 *
 * @example
 * ```tsx
 * // In parent route staticData
 * staticData: {
 *   header: {
 *     Actions: () => <DefaultActions />,
 *     showTitle: true
 *   }
 * }
 *
 * // In child route staticData
 * staticData: {
 *   header: {
 *     Actions: () => <CustomActions />,
 *     showTitle: undefined  // Explicitly override parent
 *   }
 * }
 *
 * // Usage in component
 * const { data: header } = useStaticDataMerge((data) => data.header, {})
 * // Result: { data: { Actions: CustomActions, showTitle: undefined }, refKey: '...' }
 * ```
 */
export function useStaticDataMerge<TValue>(
  selector: StaticDataSelector<TValue>,
  defaultValue?: TValue,
): { data: TValue; refKey: string } {
  return useMatches({
    select: matches => {
      let merged = (defaultValue ?? {}) as TValue

      // Iterate through matches from parent to child
      for (const match of matches) {
        if (match.staticData) {
          const value = selector(match.staticData)
          // Handle functions that need to be called with match context
          const resolvedValue = typeof value === 'function' ? value(match) : value

          if (resolvedValue !== undefined && resolvedValue !== null) {
            merged = {
              ...merged,
              ...resolvedValue,
            }
          }
        }
      }

      const refKey = matches
        .filter(m => {
          const value = selector(m.staticData ?? {})
          return value !== undefined && value !== null
        })
        .map(m => m.id)
        .join(':')

      return {
        data: merged,
        refKey,
      }
    },
  })
}
